import React from 'react';
import {
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Slider,
    FormHelperText,
    useTheme,
    OutlinedInput
} from '@mui/material';
import { tokens } from '../../../../theme';

const ReviewForm = ({ formData, onChange, hasError }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const errorTypes = [
        { value: 'incorrect_answer', label: 'Incorrect Answer' },
        { value: 'unclear_wording', label: 'Unclear Wording' },
        { value: 'missing_option', label: 'Missing Option' },
        { value: 'incorrect_explanation', label: 'Incorrect Explanation' },
        { value: 'formatting_issue', label: 'Formatting Issue' },
        { value: 'content_error', label: 'Content Error' },
        { value: 'difficulty_mismatch', label: 'Difficulty Mismatch' },
        { value: 'curriculum_mismatch', label: 'Curriculum Mismatch' },
        { value: 'other', label: 'Other' }
    ];

    const errorCategories = [
        'Grammar',
        'Spelling',
        'Mathematical',
        'Scientific',
        'Logical',
        'Factual',
        'Technical',
        'Pedagogical'
    ];

    const handleStatusChange = (event) => {
        const newStatus = event.target.value;
        onChange({ 
            status: newStatus,
            // Clear error fields if status is looks_good
            ...(newStatus === 'looks_good' && {
                feedback: '',
                error_type: '',
                error_categories: [],
                suggested_correction: '',
                severity: ''
            })
        });
    };

    const handleCategoryChange = (event) => {
        onChange({ error_categories: event.target.value });
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom color={colors.greenAccent[400]}>
                Review Assessment
            </Typography>

            {/* Status Selection */}
            <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Review Status *
                </Typography>
                <RadioGroup
                    value={formData.status}
                    onChange={handleStatusChange}
                    row
                >
                    <FormControlLabel
                        value="looks_good"
                        control={<Radio color="success" />}
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography>Looks Good</Typography>
                                <Chip label="No issues" size="small" color="success" variant="outlined" />
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="has_error"
                        control={<Radio color="error" />}
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography>Has Error</Typography>
                                <Chip label="Contains mistakes" size="small" color="error" variant="outlined" />
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="needs_revision"
                        control={<Radio color="warning" />}
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography>Needs Revision</Typography>
                                <Chip label="Requires improvement" size="small" color="warning" variant="outlined" />
                            </Box>
                        }
                    />
                </RadioGroup>
            </Box>

            {/* Conditional Fields - Show when status is not looks_good */}
            {(formData.status === 'has_error' || formData.status === 'needs_revision') && (
                <>
                    {/* Feedback */}
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Feedback *
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.feedback}
                            onChange={(e) => onChange({ feedback: e.target.value })}
                            placeholder="Describe the issue or improvement needed..."
                            variant="outlined"
                            required
                            error={!formData.feedback && formData.status !== 'looks_good'}
                            helperText={
                                !formData.feedback && formData.status !== 'looks_good' 
                                    ? "Feedback is required when reporting issues" 
                                    : `${formData.feedback.length}/5000 characters`
                            }
                            inputProps={{ maxLength: 5000 }}
                        />
                    </Box>

                    {/* Error Type - Required for has_error */}
                    {formData.status === 'has_error' && (
                        <Box mb={3}>
                            <FormControl fullWidth required error={!formData.error_type}>
                                <InputLabel>Error Type *</InputLabel>
                                <Select
                                    value={formData.error_type}
                                    onChange={(e) => onChange({ error_type: e.target.value })}
                                    label="Error Type *"
                                >
                                    <MenuItem value="">
                                        <em>Select error type</em>
                                    </MenuItem>
                                    {errorTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!formData.error_type && (
                                    <FormHelperText>Error type is required when status is "Has Error"</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    )}

                    {/* Error Categories */}
                    <Box mb={3}>
                        <FormControl fullWidth>
                            <InputLabel>Error Categories</InputLabel>
                            <Select
                                multiple
                                value={formData.error_categories}
                                onChange={handleCategoryChange}
                                input={<OutlinedInput label="Error Categories" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {errorCategories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Select all applicable categories</FormHelperText>
                        </FormControl>
                    </Box>

                    {/* Suggested Correction */}
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Suggested Correction
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.suggested_correction}
                            onChange={(e) => onChange({ suggested_correction: e.target.value })}
                            placeholder="Provide your suggested correction or improvement..."
                            variant="outlined"
                            helperText={`${formData.suggested_correction.length}/5000 characters`}
                            inputProps={{ maxLength: 5000 }}
                        />
                    </Box>

                    {/* Severity */}
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                            Severity Level
                        </Typography>
                        <RadioGroup
                            value={formData.severity}
                            onChange={(e) => onChange({ severity: e.target.value })}
                            row
                        >
                            <FormControlLabel
                                value="minor"
                                control={<Radio color="info" />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography>Minor</Typography>
                                        <Chip label="Low impact" size="small" color="info" variant="outlined" />
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="major"
                                control={<Radio color="warning" />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography>Major</Typography>
                                        <Chip label="Significant impact" size="small" color="warning" variant="outlined" />
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="critical"
                                control={<Radio color="error" />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography>Critical</Typography>
                                        <Chip label="Must fix" size="small" color="error" variant="outlined" />
                                    </Box>
                                }
                            />
                        </RadioGroup>
                    </Box>
                </>
            )}

            {/* Confidence Level - Always visible */}
            <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Confidence Level: {formData.confidence_level}
                </Typography>
                <Box px={2}>
                    <Slider
                        value={formData.confidence_level}
                        onChange={(e, value) => onChange({ confidence_level: value })}
                        min={1}
                        max={5}
                        marks={[
                            { value: 1, label: '1 - Low' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3 - Medium' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5 - High' }
                        ]}
                        valueLabelDisplay="auto"
                        color="primary"
                    />
                </Box>
                <FormHelperText>
                    How confident are you in your assessment of this question?
                </FormHelperText>
            </Box>
        </Box>
    );
};

export default ReviewForm;